import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import type { Node, ReactFlowInstance } from 'reactflow'
import ReactFlow, { ReactFlowProvider, useStore as useReactFlowStore } from 'reactflow'
import 'reactflow/dist/style.css'

import { CarNode } from '../../../components/research/carNode/CarNode'
import { useStore as useAppStore } from '../../../store/store'
import { VehicleResearchResponse } from '../../../types/vehicle'

import { buildDonateNodes } from '../utils/buildDonateNodes'
import { buildEdges } from '../utils/buildEdges'
import { buildNormalNodes } from '../utils/buildNodes'

import { DonateResearchTree } from './DonateResearchTree'
import { ResearchTreeHeader } from './researchTreeHeader/ResearchTreeHeader'


export const ResearchTreeConfig = {
  HORIZONTAL_SPACE_BETWEEN_VEHICLES: 400,
  VERTICAL_SPACE_BETWEEN_TYPES: 400,
  VERTICAL_SPACE_BETWEEN_SIBLINGS: 85,
  MIN_X: -150,
  MIN_Y: -50,
  MAX_X: 4000,
  MAX_Y: 2000,
}

const nodeTypes = { car: CarNode } as const

const DonateViewportBridge = ({ nodes }: { nodes: Node[] }) => {
  // Берём только x из transform, чтобы обновлялся только этот небольшой компонент
  const x = useReactFlowStore((s) => s.transform[0])
  return <DonateResearchTree nodes={nodes} viewportX={x} />
}

export const ResearchTree = ({
  data,
  setTriggerUpdate,
  navigate,
}: {
  data: VehicleResearchResponse[]
  setTriggerUpdate: Dispatch<SetStateAction<number>>
  navigate: NavigateFunction
}) => {
  const userId = useAppStore((state) => state.userId) || 0
  const [selectedRank, setSelectedRank] = useState(0)

  const onCarHover = useCallback((rank: number) => {
    setSelectedRank((prev) => (prev === rank ? prev : rank))
  }, [])

  const mainNodes = useMemo(
    () =>
      buildNormalNodes(
        userId,
        data,
        setTriggerUpdate,
        navigate,
        onCarHover,
      ),
    [userId, data, setTriggerUpdate, navigate, onCarHover]
  )

  const edges = useMemo(() => buildEdges(data), [data])

  const donateNodes = useMemo(
    () => buildDonateNodes(userId, data, setTriggerUpdate, onCarHover),
    [userId, data, setTriggerUpdate, onCarHover]
  )

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <ResearchTreeHeader selectedRank={selectedRank} />

        <ReactFlow
          nodes={mainNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          minZoom={1}
          maxZoom={1}
          translateExtent={[
            [ResearchTreeConfig.MIN_X, ResearchTreeConfig.MIN_Y],
            [ResearchTreeConfig.MAX_X, ResearchTreeConfig.MAX_Y],
          ]}
          onInit={(instance: ReactFlowInstance) => {
            instance.setViewport({ x: 0, y: 0, zoom: 1 })
          }}
        />

        <DonateViewportBridge nodes={donateNodes || []} />
      </ReactFlowProvider>
    </div>
  )
}